/**
 * Pure arithmetic for the clearance chart (`ClearanceChart.tsx`).
 *
 * It lives here, next to the other clearance lib modules, for the same reason
 * `stationVolumes/chartLayout.ts` does: the grouping and the tick maths are
 * assertable on their own, and a React test should not be the only thing
 * standing between a corridor-length run and 4 000 one-pixel `<rect>`s.
 *
 * Shared (`@gis/clearance`) with the report worker, so it carries NO palette:
 * the K2 colour map (`CLEARANCE_K2_KEY`, `CLEARANCE_FILL`), the legend keys,
 * the count-tick formatter and the ladder opacity ramp stay in the client's
 * `app/src/lib/clearance/chart.ts`, which re-exports everything here.
 */
import type { ClearanceBin, ClearanceNearMiss, ClearanceSummary } from './types';

/**
 * What the bar pane plots: violating points, the deepest intrusion, or the
 * near-miss points (nested by band, drawn stacked). `nearMiss` exists because
 * a MARGINAL or PASS run plots nothing but zeros under the other two — the one
 * series that matters on a clean run was not plottable at all (audit §A4).
 */
export type ClearanceMetric = 'count' | 'depth' | 'nearMiss';

/** The order the metric control lists its options in. */
export const CLEARANCE_METRICS: readonly ClearanceMetric[] = ['count', 'depth', 'nearMiss'];

/**
 * The metric a run's chart opens on (spec §B5): near misses when nothing
 * intruded — the Count bars of a clean run are all zero — else the violation
 * count. Read by the dock's initial state, so a MARGINAL run opens on the
 * chart that has something on it.
 */
export const defaultClearanceMetric = (summary: Pick<ClearanceSummary, 'violationCount'>): ClearanceMetric =>
  summary.violationCount === 0 ? 'nearMiss' : 'count';

/**
 * One drawn bar. `fromBin`/`toBin` are SOURCE bin indices, so a click on a
 * grouped bar brushes the whole range it stands for rather than one arbitrary
 * member — the same contract `SuperBin` gives the station chart.
 */
export interface ClearanceSuperBin {
  i: number;
  fromBin: number;
  toBin: number;
  /**
   * The group's INTERNAL station span (ruling P6). The display stations below
   * are what the axis prints; these are what the alignment, the walk cursor
   * and `clipBoxAtStation` understand. A click on a bar walks to
   * `binCentreStation(b)`, so the super-bin has to carry them — converting a
   * display station back through the run's equations inside a click handler
   * would be the same arithmetic done twice, in the wrong place.
   */
  staStartInternalM: number;
  staEndInternalM: number;
  staStartDisplayM: number;
  staEndDisplayM: number;
  pointCount: number;
  violationCount: number;
  /** null only when NO member bin had a violation. */
  maxIntrusionM: number | null;
  /**
   * Where the deepest intrusion sat in the section — the WORST member bin's
   * figures, never a mean or a mix of two bins (an offset from one bin and a
   * height from another would describe a point nobody measured). `null` when
   * no member violated.
   *
   * Optional only so hand-built test fixtures that predate the tooltip keep
   * type-checking; `groupClearanceBins` always sets both.
   */
  worstOffsetM?: number | null;
  worstHeightM?: number | null;
  nearMiss: ClearanceNearMiss;
}

const asSuper = (b: ClearanceBin, i: number): ClearanceSuperBin => ({
  i,
  fromBin: b.i,
  toBin: b.i,
  staStartInternalM: b.staStartInternalM,
  staEndInternalM: b.staEndInternalM,
  staStartDisplayM: b.staStartDisplayM,
  staEndDisplayM: b.staEndDisplayM,
  pointCount: b.pointCount,
  violationCount: b.violationCount,
  maxIntrusionM: b.maxIntrusionM,
  worstOffsetM: b.worstOffsetM,
  worstHeightM: b.worstHeightM,
  nearMiss: b.nearMiss,
});

/**
 * Aggregate `bins` down to at most `maxBars` bars.
 *
 * Counts SUM (exact — a violation counted in two adjacent bins is two
 * violations) and the intrusion depth takes the MAX, never a mean: the deepest
 * intrusion in a 200 m stretch is the fact an engineer walks out to, and
 * averaging it away is how a chart hides the one bin that matters. Near-miss
 * counts sum like the violations; they stay nested afterwards because each
 * member's ladder was already nested.
 */
export function groupClearanceBins(
  bins: readonly ClearanceBin[],
  maxBars: number,
): { bins: ClearanceSuperBin[]; groupSize: number } {
  const n = bins.length;
  const cap = Math.max(1, Math.floor(maxBars));
  if (n === 0) return { bins: [], groupSize: 1 };
  if (n <= cap) return { bins: bins.map(asSuper), groupSize: 1 };

  const groupSize = Math.ceil(n / cap);
  const out: ClearanceSuperBin[] = [];
  for (let start = 0, k = 0; start < n; start += groupSize, k++) {
    const part = bins.slice(start, Math.min(start + groupSize, n));
    const last = part[part.length - 1];
    let violationCount = 0;
    let pointCount = 0;
    let worst: number | null = null;
    let worstOffsetM: number | null = null;
    let worstHeightM: number | null = null;
    const nearMiss: ClearanceNearMiss = { within0_1M: 0, within0_2M: 0, within0_35M: 0 };
    for (const b of part) {
      violationCount += b.violationCount;
      pointCount += b.pointCount;
      if (b.maxIntrusionM !== null && (worst === null || b.maxIntrusionM > worst)) {
        worst = b.maxIntrusionM;
        worstOffsetM = b.worstOffsetM;
        worstHeightM = b.worstHeightM;
      }
      nearMiss.within0_1M += b.nearMiss.within0_1M;
      nearMiss.within0_2M += b.nearMiss.within0_2M;
      nearMiss.within0_35M += b.nearMiss.within0_35M;
    }
    out.push({
      i: k,
      fromBin: part[0].i,
      toBin: last.i,
      staStartInternalM: part[0].staStartInternalM,
      staEndInternalM: last.staEndInternalM,
      staStartDisplayM: part[0].staStartDisplayM,
      staEndDisplayM: last.staEndDisplayM,
      pointCount,
      violationCount,
      maxIntrusionM: worst,
      worstOffsetM,
      worstHeightM,
      nearMiss,
    });
  }
  return { bins: out, groupSize };
}

/**
 * The plotted value of a bar under the active metric. For `nearMiss` it is the
 * OUTERMOST rung — the ladder is nested, so `within0_35M` is the whole stack
 * and the inner rungs are drawn inside it (`nearMissStackPx`).
 */
export const metricValue = (b: ClearanceSuperBin, metric: ClearanceMetric): number =>
  metric === 'count'
    ? b.violationCount
    : metric === 'nearMiss'
      ? b.nearMiss.within0_35M
      : (b.maxIntrusionM ?? 0);

/** Whether the metric's axis counts points (integer ticks, count formatter). */
export const isCountMetric = (metric: ClearanceMetric): boolean => metric !== 'depth';

/**
 * `Math.max(...xs)` spreads its argument list onto the stack and throws
 * `RangeError` somewhere north of ~100 k arguments. A corridor-length run at a
 * 1 m interval is well inside that, but the reduce costs nothing and removes
 * the cliff entirely.
 */
export function metricMax(bins: readonly ClearanceSuperBin[], metric: ClearanceMetric): number {
  let peak = 0;
  for (const b of bins) peak = Math.max(peak, metricValue(b, metric));
  // Never zero: it is the divisor for every bar height.
  return peak > 0 ? peak : 1;
}

/**
 * Whether a bar carries any signal at all, and of which kind. Drives both the
 * fill and the minimum drawn height — ruling K2's near-miss colouring is only
 * a colouring if the shape it colours has a non-zero height, and a near-miss
 * bin plots zero under BOTH metrics (no violations to count, no intrusion
 * depth to measure).
 */
export type ClearanceBarKind = 'violation' | 'nearMiss' | 'clear';

/**
 * The verdict thresholds (owner ruling, 2026-10-20), as SHARES of the points
 * the sweep scored in the interval (or the run):
 *
 *  - PASS / clear — nothing inside, and under 5 % in the outermost band;
 *  - MARGINAL / nearMiss — under 2 % inside, and under 10 % in the band;
 *  - FAIL / violation — everything else (2 % or more inside, or 10 % or more
 *    in the band even with nothing inside).
 *
 * "In the band" is `within0_35M` — the OUTERMOST rung of the nested ladder,
 * which never counts a point that is inside (that is `violationCount`).
 */
export const CLEARANCE_FAIL_INSIDE_SHARE = 0.02;
export const CLEARANCE_PASS_BAND_SHARE = 0.05;
export const CLEARANCE_MARGINAL_BAND_SHARE = 0.1;

/**
 * The K2 ladder, as ONE predicate (ruling P14). Structural on purpose: a
 * `ClearanceSuperBin`, a `ClearanceBin` and anything else carrying the scored
 * count, a violation count and the near-miss ladder all satisfy it, so the
 * verdict, the chart's bars, the 3D corridor plates (`clearanceBandState`),
 * the "flagged" page filter and both legends escalate on exactly the same
 * evidence. Duplicating it once was already enough to put a bin amber in the
 * scene and neutral in the chart.
 *
 * Thresholds are shares of `pointCount` — see `CLEARANCE_FAIL_INSIDE_SHARE`.
 * An interval that scored nothing is `clear` here; callers that must tell
 * "empty" apart check `pointCount` first.
 */
export const k2Verdict = (b: {
  pointCount: number;
  violationCount: number;
  nearMiss: Pick<ClearanceNearMiss, 'within0_35M'>;
}): ClearanceBarKind => {
  if (!(b.pointCount > 0)) return 'clear';
  const inside = b.violationCount / b.pointCount;
  const band = b.nearMiss.within0_35M / b.pointCount;
  if (b.violationCount === 0 && band < CLEARANCE_PASS_BAND_SHARE) return 'clear';
  if (inside < CLEARANCE_FAIL_INSIDE_SHARE && band < CLEARANCE_MARGINAL_BAND_SHARE) return 'nearMiss';
  return 'violation';
};

/** The chart's name for `k2Verdict`. Not a wrapper — the same function. */
export const barKind: (b: ClearanceSuperBin) => ClearanceBarKind = k2Verdict;

/**
 * Minimum drawn height in px per kind. Only a VIOLATION gets a floor, and only
 * under the depth metric's sibling rule of "a real value must survive
 * rounding": a bin with nothing to plot draws NO bar (audit §A4 — a 2–3 px
 * stub on every clean bin read as a dashed baseline, noise with no
 * magnitude). Near-miss status is not lost: the chart marks it as a 3 px
 * amber tick UNDER the baseline (`NEAR_MISS_TICK_PX`), and the Near-miss
 * metric plots it with a real height.
 */
export const BAR_FLOOR_PX: Record<ClearanceBarKind, number> = {
  violation: 1,
  nearMiss: 0,
  clear: 0,
};

/** The near-miss status tick drawn under the baseline, in px. */
export const NEAR_MISS_TICK_PX = 3;

/**
 * Drawn bar height: the scaled value, floored only when there IS a value — a
 * bar whose plotted value is zero draws nothing, whatever its kind.
 */
export function barHeightPx(b: ClearanceSuperBin, metric: ClearanceMetric, maxValue: number, innerH: number): number {
  const v = metricValue(b, metric);
  if (!(v > 0)) return 0;
  const scaled = (v / maxValue) * innerH;
  // The floor applies to whatever the metric plots: a near-miss bar under the
  // Near-miss metric is a real value too.
  return Math.max(1, BAR_FLOOR_PX[barKind(b)], scaled);
}

/**
 * The Near-miss metric's nested stack, as px heights from the baseline,
 * OUTERMOST first (the order they are painted: each inner rung is drawn over
 * the one outside it). The counts are nested (`within0_1M ⊂ 0_2M ⊂ 0_35M`), so
 * the stack's total is the outer rung's count and each inner segment is the
 * share of the bar that sat closer still — the bar is not three counts added.
 */
export function nearMissStackPx(b: ClearanceSuperBin, maxValue: number, innerH: number): number[] {
  return [...NEAR_MISS_RUNGS].reverse().map((k) => {
    const n = b.nearMiss[k];
    return n > 0 ? Math.max(1, (n / maxValue) * innerH) : 0;
  });
}

/**
 * Round tick values from 0 to `maxValue`, at most `count` of them.
 * `integersOnly` (the count metric) never emits `3.5 violations`.
 */
export function axisTicks(maxValue: number, count: number, integersOnly: boolean): number[] {
  if (!(maxValue > 0) || count < 2) return [0];
  const raw = maxValue / (count - 1);
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  // Nearest nice step, not the next one UP: rounding up turns a max of 7 into a
  // single `5` gridline and an axis with two labels on it, which is worse than
  // no axis. The 1.5 divisor is what makes the choice "nearest on a log scale".
  const step0 = [1, 2, 5, 10].map((m) => m * mag).find((s) => s >= raw / 1.5) ?? 10 * mag;
  const step = integersOnly ? Math.max(1, Math.round(step0)) : step0;
  const out: number[] = [];
  for (let v = 0; v <= maxValue + step * 1e-6; v += step) out.push(Math.round(v * 1e6) / 1e6);
  return out;
}

/**
 * The super-bin under an INTERNAL station, by binary search over the drawn
 * bars' own spans — the chart's pointer hit test. Structural, so the SOURCE
 * bins answer the same question (which interval the walk or the pointer is
 * in) without assuming every interval is `stationIntervalM` long. A station before the first
 * bar or past the last clamps to that end, so the overlay's edge pixels still
 * land on a bar rather than on nothing. `-1` only when there are no bars.
 */
export function superBinIndexAt(
  bins: readonly Pick<ClearanceSuperBin, 'staStartInternalM'>[],
  station: number,
): number {
  if (bins.length === 0) return -1;
  let lo = 0;
  let hi = bins.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (bins[mid].staStartInternalM <= station) lo = mid;
    else hi = mid - 1;
  }
  return lo;
}

/**
 * The near-miss ladder's three rungs, innermost first — the order the ladder
 * pane draws its rows in, top to bottom. The counts are NESTED (each rung
 * includes the ones inside it), and the ladder draws them as reported:
 * row "≤ 0.2 m" is every point within 0.2 m, not the 0.1–0.2 m slice. The
 * table's columns say the same thing in the same words.
 */
export const NEAR_MISS_RUNGS = ['within0_1M', 'within0_2M', 'within0_35M'] as const satisfies readonly (keyof ClearanceNearMiss)[];

/**
 * v1's bands, for a run whose params predate `nearMissBandsM`. The ONE copy —
 * the table, the section and the chart all import it.
 */
export const DEFAULT_NEAR_MISS_BANDS_M: readonly number[] = [0.1, 0.2, 0.35];

/** A run's near-miss bands, falling back to v1's for a run that predates them. */
export const nearMissBandsOf = (params: { nearMissBandsM?: readonly number[] | null }): readonly number[] =>
  params.nearMissBandsM?.length ? params.nearMissBandsM : DEFAULT_NEAR_MISS_BANDS_M;

/**
 * The share of an interval's SCORED points that sat within a band — what a
 * ladder cell is shaded by. `null` when the interval scored nothing (a share
 * of nothing is not zero, it is undefined).
 */
export function ladderShare(n: number, pointCount: number): number | null {
  if (!(pointCount > 0)) return null;
  return Math.max(0, Math.min(1, n / pointCount));
}
